<template>
  <TreePicker
    :options="lazyOptions"
    :value="value"
    searchable
    :placeholder="placeholder"
    :unassigned-value="unassignedValue"
    :multiple="multiple"
    :full-path="fullPath"
    v-bind="$attrs"
    :as-input="asInput"
    :only-leaf-node-selectable="isOnlyLeafNodeSelectable"
    @blur="$emit('blur')"
    @change="$emit('change', $event)"
    @selected="$emit('selected', $event)"
    @show="hasOpened = true"
  >
    <template v-slot:trigger="slotData">
      <slot name="trigger" v-bind="slotData"> </slot>
    </template>
  </TreePicker>
</template>

<script>
import SortBy from 'lodash/sortBy'
import { flattenRecursive, buildHierarchy } from '@data/recursive'
import {
  DepartmentComputed,
  getDepartmentsApi,
} from '@state/modules/department'
import { buildRelationalQualificationStructure } from '@data/qualification'
import TreePicker from '@components/tree-picker/tree-picker'

export default {
  name: 'FlotoDepartmentPicker',
  components: { TreePicker },
  model: { event: 'change' },

  props: {
    moduleName: { type: String, default: undefined },
    validateArchivedValue: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    placeholder: {
      type: String,
      default() {
        return this.$tc('select')
      },
    },
    unassignedValue: { type: [String, Number], default: 0 },
    value: { type: [Number, Array, String], default: undefined },
    // eslint-disable-next-line
    asInput: { type: Boolean, default: true },
    // eslint-disable-next-line
    onlyLeafNodeSelectable: { type: Boolean, default: true },
    fullPath: { type: Boolean, default: false },
    ignoreHeirarchyConfig: { type: Boolean, default: false },
  },
  data() {
    return {
      archivedOptions: [],
      hasOpened: false,
    }
  },
  computed: {
    ...DepartmentComputed,
    transformedModuleName() {
      if (this.moduleName === this.$constants.SERVICE_CATALOG) {
        return this.$constants.REQUEST
      }
      return this.moduleName
    },
    isOnlyLeafNodeSelectable() {
      if (this.onlyLeafNodeSelectable && this.transformedModuleName) {
        if (this.ignoreHeirarchyConfig) {
          return this.onlyLeafNodeSelectable
        }
        return (
          this[`${this.transformedModuleName}OnlyLeafNodeSelectable`] || false
        )
      }
      return false
    },
    lazyOptions() {
      // Before opening: return minimal (selected items only - FAST)
      // After opening: return full tree (SLOW but only once)
      const baseOptions = this.hasOpened
        ? this.fullOptions
        : this.minimalOptions

      if (this.$attrs['additional-options']) {
        return (this.$attrs['additional-options'] || []).concat(baseOptions)
      }
      return baseOptions
    },
    minimalOptions() {
      // Lightweight: just selected items for display
      if (!this.value) return this.archivedOptions

      const rawDepartments = this.departmentOptions
      const flatOptions = flattenRecursive(rawDepartments)
      const valueArray = Array.isArray(this.value) ? this.value : [this.value]
      const selected = flatOptions.filter((o) => valueArray.includes(o.id))

      return [...selected, ...this.archivedOptions]
    },
    fullOptions() {
      // Full expensive computation - buildHierarchy, sort, filter
      const options = this.departmentOptions
      const flattenedOptions = flattenRecursive(options)
      const flatOptions = SortBy(
        flattenedOptions.filter((c) => !c.archived),
        'order'
      )
      const value = Array.isArray(this.value) ? this.value : [this.value]
      const selectedOptions = value.length
        ? flattenedOptions
            .filter((o) => value.indexOf(o.id) >= 0)
            .map((o) => ({ ...o, disabled: true }))
        : []
      let hierarchy = buildHierarchy(flatOptions)
      if (selectedOptions.length) {
        const availableIds = flatOptions.map((f) => f.id)
        hierarchy = hierarchy.concat(
          selectedOptions.filter((o) => availableIds.indexOf(o.id) === -1)
        )
      }
      return hierarchy.concat(this.archivedOptions)
    },
    availableOptions() {
      if (this.$attrs['additional-options']) {
        return (this.$attrs['additional-options'] || []).concat(
          this.fullOptions
        )
      }
      return this.fullOptions
    },
  },
  created() {
    // Check for missing departments using raw data (fast)
    if (this.value) {
      const v = Array.isArray(this.value) ? this.value : [this.value]
      const rawDepartments = this.departmentOptions
      const flattenedOptions = flattenRecursive(rawDepartments)
      const allAvailableUserIds = flattenedOptions.map((u) => u.id)
      const missingDepartments = v.filter(
        (userId) => allAvailableUserIds.indexOf(userId) === -1 && !isNaN(userId)
      )
      if (missingDepartments.length) {
        if (this.validateArchivedValue && !this.multiple) {
          this.$emit('change', this.unassignedValue)
        } else {
          this.fetchMissingDepartments(missingDepartments)
        }
      }
    }
  },
  methods: {
    fetchMissingDepartments(departmentIds) {
      getDepartmentsApi(
        {
          quals: [
            buildRelationalQualificationStructure(
              'id',
              'in',
              departmentIds,
              'long',
              'db'
            ),
          ],
        },
        undefined,
        undefined,
        { archived: true }
      ).then((data) => {
        this.archivedOptions = Object.freeze(
          (data.items || []).map((c) => ({
            ...c,
            disabled: c.archived || c.disabled,
          }))
        )
      })
    },
  },
}
</script>
